import { useState } from "react";
import { useApp } from "../context/AppContext";

export default function AuthPage({ onAuthenticated }) {
  const { signIn, signUp, signInWithGoogle } = useApp();

  // "welcome" = landing screen, "signin"/"signup" = auth form
  const [mode,       setMode]       = useState("welcome");
  const [isEmployer, setIsEmployer] = useState(false); // always start as job seeker on welcome screen
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [name,     setName]     = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(null);

  const isSignUp = mode === "signup";

  const startAsJobSeeker = () => {
    sessionStorage.setItem("vorsia-portal-choice", "jobseeker");
    setIsEmployer(false);
    localStorage.removeItem("vorsia-employer-signup");
    setMode("signup");
  };

  const startAsEmployer = () => {
    sessionStorage.setItem("vorsia-portal-choice", "employer");
    setIsEmployer(true);
    localStorage.setItem("vorsia-employer-signup", "true");
    setMode("signup");
  };

  const handleSubmit = async () => {
    setError(null);
    if (!email.trim() || !password.trim()) return setError("Please fill in all fields.");
    if (isSignUp && !name.trim()) return setError("Please enter your name.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    setLoading(true);
    try {
      if (isSignUp) {
        const { data, error } = await signUp(email, password, { full_name: name });
        if (!error && isEmployer && data.user) {
            const { supabase } = await import("../lib/supabase");
          await supabase.from("profiles").upsert({ id: data.user.id, role: "employer", onboarded: true, full_name: name });
          localStorage.removeItem("vorsia-employer-signup");
        }
        if (error) throw error;
        if (data.user && !data.user.email_confirmed_at) {
          setSuccess("Check your email to confirm your account, then sign in.");
        } else {
          onAuthenticated(data.user);
        }
      } else {
        const { data, error } = await signIn(email, password);
        if (error) throw error;
        onAuthenticated(data.user);
      }
    } catch (err) {
      const msg = err.message?.toLowerCase() || "";
      if (msg.includes("invalid login") || msg.includes("invalid credentials") || msg.includes("wrong password")) {
        setError("Incorrect email or password. Please try again.");
      } else if (msg.includes("email not confirmed")) {
        setError("Please confirm your email before signing in.");
      } else if (msg.includes("already registered") || msg.includes("already exists") || msg.includes("unique")) {
        setError("An account with this email already exists. Try signing in.");
      } else if (msg.includes("rate limit") || msg.includes("too many")) {
        setError("Too many attempts. Please wait a moment and try again.");
      } else if (msg.includes("network") || msg.includes("fetch")) {
        setError("Connection error. Please check your internet and try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setLoading(true);
    if (isEmployer) localStorage.setItem("vorsia-employer-signup", "true");
    const { error } = await signInWithGoogle();
    if (error) { setError("Google sign in failed. Please try again."); setLoading(false); }
  };

  const inputStyle = {
    width: "100%",
    background: "var(--bg-input)",
    border: "1.5px solid var(--border)",
    borderRadius: 11,
    padding: "13px 16px",
    color: "var(--text-primary)",
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 14,
    outline: "none",
  };

  // ── Welcome / landing screen ──────────────────────────────────────────────
  const WelcomeScreen = (
    <div style={{ textAlign:"center" }}>
      <img src="/vorsia_ai.png" alt="Vorsia" style={{ width:260, height:"auto", margin:"0 auto 12px", display:"block", animation:"bounceIn 0.5s cubic-bezier(0.34,1.56,0.64,1)" }} />
      <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:38, fontWeight:700, color:"var(--text-primary)", lineHeight:1.15, marginBottom:10 }}>
        Welcome to<br />
        <em style={{ fontStyle:"italic", color:"var(--accent)" }}>Vorsia.</em>
      </h2>
      <p style={{ color:"var(--text-secondary)", fontSize:14, marginBottom:36, lineHeight:1.6 }}>
        Your AI-Powered Career Platform. Set up in under 60 seconds.
      </p>

      {/* Two CTA buttons */}
      <div style={{ display:"flex", flexDirection:"column", gap:12, maxWidth:340, margin:"0 auto" }}>
        <button onClick={startAsJobSeeker}
          style={{ width:"100%", background:"linear-gradient(135deg, #29ABE2, #9B35C8)", border:"none", color:"#fff", padding:"15px 24px", borderRadius:12, fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          Get Started →
          <span style={{ fontSize:12, fontWeight:400, opacity:0.8 }}>I'm looking for a job</span>
        </button>

        <button onClick={startAsEmployer}
          style={{ width:"100%", background:"var(--bg-card)", border:"1.5px solid var(--border)", color:"var(--text-primary)", padding:"15px 24px", borderRadius:12, fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8, transition:"border-color 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.borderColor="rgba(123,111,232,0.4)"}
          onMouseLeave={e => e.currentTarget.style.borderColor="var(--border)"}>
          🏢 I'm Hiring →
          <span style={{ fontSize:12, fontWeight:400, opacity:0.6 }}>Post jobs & find talent</span>
        </button>
      </div>

      {/* Already have account */}
      <p style={{ marginTop:28, fontSize:13, color:"var(--text-tertiary)" }}>
        Already have an account?{" "}
        <button onClick={() => { localStorage.removeItem("vorsia-employer-signup"); sessionStorage.removeItem("vorsia-portal-choice"); setIsEmployer(false); setMode("signin"); }}
          style={{ background:"none", border:"none", color:"var(--accent)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, padding:0 }}>
          Sign in
        </button>
      </p>

      {/* About link */}
      <p style={{ marginTop:10, fontSize:12, color:"var(--text-tertiary)" }}>
        <a href="/?page=about"
          style={{ color:"var(--text-tertiary)", textDecoration:"none", fontFamily:"'DM Sans',sans-serif" }}
          onMouseEnter={e => e.target.style.color = "var(--accent)"}
          onMouseLeave={e => e.target.style.color = "var(--text-tertiary)"}>
          Learn about Vorsia →
        </a>
      </p>
    </div>
  );

  // ── Auth form ─────────────────────────────────────────────────────────────
  const AuthForm = (
    <div>
      {/* Back button */}
      <button onClick={() => { setMode("welcome"); setError(null); setSuccess(null); }}
        style={{ background:"none", border:"none", color:"var(--text-tertiary)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontSize:13, padding:"0 0 20px 0", display:"flex", alignItems:"center", gap:6 }}>
        ← Back
      </button>

      {/* Logo + subtitle */}
      <div style={{ textAlign:"center", marginBottom:24 }}>
        <img src="/vorsia_ai.png" alt="Vorsia" style={{ width:200, height:"auto", margin:"0 auto 8px", display:"block" }} />
        <p style={{ color:"var(--text-tertiary)", fontSize:13, marginTop:4 }}>
          {isEmployer
            ? (isSignUp ? "Create your employer account." : "Sign in to your employer portal.")
            : (isSignUp ? "Set up in under 60 seconds." : "Sign in to continue your job search.")}
        </p>
        {isEmployer && (
          <div style={{ marginTop:8, fontSize:12, background:"var(--accent-soft)", border:"1px solid rgba(123,111,232,0.2)", borderRadius:8, padding:"6px 14px", color:"var(--accent)", fontWeight:600, display:"inline-block" }}>
            🏢 Employer Account
          </div>
        )}
      </div>

      {/* Card */}
      <div style={{ background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:18, padding:"28px 28px 24px" }}>
        {/* Google */}
        <button onClick={handleGoogle} disabled={loading}
          style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:10, background:"var(--bg-hover)", border:"1.5px solid var(--border)", borderRadius:11, padding:"12px", cursor:"pointer", color:"var(--text-primary)", fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:600, marginBottom:20 }}>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <div style={{ flex:1, height:1, background:"var(--border)" }} />
          <span style={{ fontSize:11, color:"var(--text-tertiary)", letterSpacing:1 }}>OR</span>
          <div style={{ flex:1, height:1, background:"var(--border)" }} />
        </div>

        {/* Inputs */}
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {isSignUp && (
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" style={inputStyle} />
          )}
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" style={inputStyle} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" style={inputStyle} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
        </div>

        {error && (
          <div style={{ marginTop:14, padding:"10px 14px", borderRadius:9, background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.2)", fontSize:13, color:"#f87171", animation:"fadeIn 0.2s ease" }}>{error}</div>
        )}
        {success && (
          <div style={{ marginTop:14, padding:"10px 14px", borderRadius:9, background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.2)", fontSize:13, color:"#4ade80", animation:"fadeIn 0.2s ease" }}>{success}</div>
        )}

        <button onClick={handleSubmit} disabled={loading}
          style={{ marginTop:18, width:"100%", background:loading?"var(--bg-hover)":"linear-gradient(135deg, #29ABE2, #9B35C8)", border:"none", color:loading?"var(--text-tertiary)":"#fff", padding:"13px", borderRadius:11, fontWeight:700, fontSize:15, cursor:loading?"not-allowed":"pointer", fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          {loading ? (
            <><div style={{ width:15, height:15, border:"2px solid var(--border)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />{isSignUp ? "Creating account…" : "Signing in…"}</>
          ) : isSignUp ? "Create Account →" : "Sign In →"}
        </button>

        {/* Toggle sign in / sign up */}
        <p style={{ textAlign:"center", marginTop:16, marginBottom:0, fontSize:13, color:"var(--text-tertiary)" }}>
          {isSignUp ? "Already have an account? " : "Don't have an account? "}
          <button onClick={() => { setMode(isSignUp ? "signin" : "signup"); setError(null); setSuccess(null); }}
            style={{ background:"none", border:"none", color:"#7B6FE8", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600, padding:0 }}>
            {isSignUp ? "Sign in" : "Create one"}
          </button>
        </p>

        {/* Employer / job seeker toggle */}
        <div style={{ textAlign:"center", marginTop:14, paddingTop:14, borderTop:"1px solid var(--border-soft)" }}>
          {isEmployer ? (
            <>
              <span style={{ fontSize:12, color:"var(--text-tertiary)" }}>Looking for a job? </span>
              <button onClick={() => { setIsEmployer(false); localStorage.removeItem("vorsia-employer-signup"); }}
                style={{ fontSize:12, color:"var(--accent)", fontWeight:600, background:"none", border:"none", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                Job seeker sign in →
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize:12, color:"var(--text-tertiary)" }}>Are you an employer? </span>
              <button onClick={() => { setIsEmployer(true); setMode("signup"); localStorage.setItem("vorsia-employer-signup", "true"); }}
                style={{ fontSize:12, color:"var(--accent)", fontWeight:600, background:"none", border:"none", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                Post a role on Vorsia →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg-primary)", display:"flex", alignItems:"flex-start", justifyContent:"center", position:"relative", overflowY:"auto" }}>
      {/* Grid background */}
      <div style={{ position:"fixed", inset:0, backgroundImage:"linear-gradient(var(--border-soft) 1px,transparent 1px),linear-gradient(90deg,var(--border-soft) 1px,transparent 1px)", backgroundSize:"48px 48px", opacity:0.5, pointerEvents:"none" }} />
      {/* Glow orbs */}
      <div style={{ position:"fixed", width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,rgba(232,97,58,0.07),transparent 70%)", top:-100, right:-80, pointerEvents:"none" }} />
      <div style={{ position:"fixed", width:400, height:400, borderRadius:"50%", background:"radial-gradient(circle,rgba(94,106,210,0.05),transparent 70%)", bottom:-100, left:-80, pointerEvents:"none" }} />

      <div style={{ position:"relative", zIndex:1, width:"100%", maxWidth:460, padding:"64px 24px 48px" }}>
        {mode === "welcome" ? WelcomeScreen : AuthForm}
      </div>
    </div>
  );
}
